import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Users, Search, RefreshCw, X } from 'lucide-react';
import { format } from 'date-fns';
import { api } from '../../lib/api';
import { Card, LoadingState, EmptyState, ErrorState } from '../../components/control-center/StateWrappers';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const fmtDate = (iso) => { try { return iso ? format(new Date(iso), 'dd/MM/yyyy') : '—'; } catch { return '—'; } };
const val = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return null; };

const Clients = () => {
  const [state, setState] = useState({ loading: true, error: false, rows: [] });
  const [search, setSearch] = useState('');
  const [lang, setLang] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: false }));
    try {
      const { data } = await api.get('/control-center/clients');
      setState({ loading: false, error: false, rows: Array.isArray(data) ? data : [] });
    } catch { setState({ loading: false, error: true, rows: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const languages = useMemo(() => {
    const s = new Set();
    state.rows.forEach((r) => { const l = val(r, 'idioma', 'language'); if (l) s.add(l); });
    return Array.from(s);
  }, [state.rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return state.rows.filter((r) => {
      const name  = (val(r,'nombre','name') || '').toLowerCase();
      const email = (val(r,'email') || '').toLowerCase();
      const lg = val(r,'idioma','language') || '';
      if (q && !name.includes(q) && !email.includes(q)) return false;
      if (lang && lg !== lang) return false;
      return true;
    });
  }, [state.rows, search, lang]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-start sm:items-center justify-between flex-col sm:flex-row gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Clientes</h1>
          <p className="text-slate-500 text-sm mt-1">Clientes detectados por la automatización.</p>
        </div>
        <Button onClick={load} className="bg-white text-slate-800 border border-slate-200 hover:bg-slate-50 gap-2">
          <RefreshCw className={`w-4 h-4 ${state.loading ? 'animate-spin' : ''}`} /> Actualizar
        </Button>
      </div>

      <Card className="p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por nombre o email…" className="pl-9 border-2 border-slate-200 focus:border-blue-500" data-testid="clients-search" />
        </div>
        <select value={lang} onChange={(e)=>setLang(e.target.value)} className="border-2 border-slate-200 rounded-md h-10 px-3 text-sm focus:border-blue-500 focus:outline-none" data-testid="clients-lang">
          <option value="">Todos los idiomas</option>
          {languages.map((l)=>(<option key={l} value={l}>{l}</option>))}
        </select>
      </Card>

      <Card className="overflow-hidden">
        {state.loading && <LoadingState />}
        {state.error && !state.loading && <ErrorState onRetry={load} />}
        {!state.loading && !state.error && filtered.length === 0 && (
          <EmptyState title="Sin clientes" description="Aún no hay clientes registrados por la automatización." icon={Users} />
        )}
        {!state.loading && !state.error && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Idioma</th>
                  <th className="text-left px-4 py-3 font-semibold">Estancias</th>
                  <th className="text-left px-4 py-3 font-semibold">Primera</th>
                  <th className="text-left px-4 py-3 font-semibold">Última</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i)=>(
                  <tr key={i} onClick={()=>setSelected(r)} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer" data-testid={`client-row-${i}`}>
                    <td className="px-4 py-3 text-slate-900 font-medium">{val(r,'nombre','name') || '—'}</td>
                    <td className="px-4 py-3 text-slate-700 truncate max-w-[240px]">{val(r,'email') || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{val(r,'idioma','language') || '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{val(r,'numero_estancias','stays','stays_count') ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(val(r,'first_seen_at','first_seen'))}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDate(val(r,'last_seen_at','last_seen'))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
          <div className="flex-1 bg-slate-900/40" onClick={()=>setSelected(null)} />
          <aside className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-slate-200 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Cliente</p>
                <h2 className="text-lg font-bold text-slate-900 truncate">{val(selected,'nombre','name') || '—'}</h2>
              </div>
              <button onClick={()=>setSelected(null)} className="p-2 rounded-lg hover:bg-slate-100" aria-label="Cerrar"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <Row label="Email" value={val(selected,'email')} />
              <Row label="Idioma" value={val(selected,'idioma','language')} />
              <Row label="Número de estancias" value={val(selected,'numero_estancias','stays','stays_count')} />
              <Row label="Primera vez" value={fmtDate(val(selected,'first_seen_at','first_seen'))} />
              <Row label="Última vez" value={fmtDate(val(selected,'last_seen_at','last_seen'))} />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Preferencias</p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-800 whitespace-pre-wrap text-sm">
                  {(() => {
                    const p = val(selected,'preferencias','preferences');
                    if (!p) return '—';
                    if (typeof p === 'string') return p;
                    try { return JSON.stringify(p, null, 2); } catch { return '—'; }
                  })()}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium text-right break-words">{value ?? '—'}</span>
  </div>
);

export default Clients;
